// @ts-check
// Configurações > Canais: os números de WhatsApp pela API oficial (um cartão por número, "Conectar com a Meta" pelo
// portal e a conexão manual recolhida, contrato 08 de leads e WhatsApp, seção 8.1), o resumo dos modelos e o endereço
// de e-mail de entrada com botão de copiar. Desde a 2.0.0, cada número mostra o limite diário da Meta e as retenções
// dos envios em massa (contrato 09 de campanhas de WhatsApp, seção 3). Desde a 3.0.0, com o módulo Atendimento ligado,
// cada número e o e-mail de entrada ganham a finalidade (Captação, Atendimento ou Misto) e o departamento padrão
// (contrato 10 de atendimento, seções 4.1 e 24.4).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { hasCurrentPermissionFlag, isTenantModuleEnabled } from '../../data/metadata-store.js';
import { listServiceTeams } from '../../data/service-desk.js';
import { ChannelPurposeField } from '../service-desk/channel-purpose-field.js';
import { connectWhatsappChannel, deleteChannel, listChannels } from '../../data/conversations.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { ChannelIcon } from '../conversations/conversation-display.js';
import { hasTranslation, t } from '../../i18n/index.js';
import { LeadAdsCard } from './channel-integrations.js';
import { AdminOnlyNote } from './integration-ui.js';
import { registerWhatsappNumber } from '../../data/integrations.js';
import { MetaSignupConnect } from './meta-signup/meta-signup-connect.js';
import { WhatsappTemplatesSummary } from './whatsapp-templates/whatsapp-templates-summary.js';
import { WhatsappMessagingLimit } from '../whatsapp-campaigns/whatsapp-messaging-limit.js';
import { OptOutConfirmationToggle } from '../whatsapp-campaigns/opt-out-confirmation-toggle.js';

/**
 * @typedef {import('../../data/conversations.js').Channel} Channel
 * @typedef {import('./meta-signup/meta-signup-connect.js').MetaSignupResult} MetaSignupResult
 */

// cadastro da forma de pagamento dos modelos (cobrança da Meta, não do Matrícula Mais)
const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/home/';

const WHATSAPP_FORM_FIELDS = /** @type {const} */ (['phoneNumberId', 'wabaId', 'displayPhoneNumber', 'verifiedName', 'accessToken']);

/**
 * @param {string} text
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Sem permissão da área de transferência: seleção manual com execCommand.
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.append(textarea);
    textarea.select();
    const isCopied = document.execCommand('copy');
    textarea.remove();
    return isCopied;
  }
}

/**
 * @param {{ value: string, label: string }} props
 */
export function CopyableValue({ value, label }) {
  return html`<div class="copyable-value">
    <code class="copyable-value-text" title=${value}>${value}</code>
    <${Button}
      title=${t('settings.channels.email.copy')}
      icon="IconCopy"
      variant="secondary"
      size="small"
      ariaLabel=${label}
      onClick=${async () => {
        if (await copyToClipboard(value)) showSuccessSnackbar(t('settings.channels.copied'));
      }}
    />
  </div>`;
}

/**
 * @param {{ isConnected: boolean, label?: string }} props
 */
function ConnectionStatus({ isConnected, label }) {
  return html`<span class="channel-status" data-connected=${isConnected ? 'true' : 'false'}>
    <span class="channel-status-dot"></span>
    ${label ?? (isConnected ? t('settings.channels.whatsapp.connected') : t('settings.channels.whatsapp.disconnected'))}
  </span>`;
}

/** @param {{ tone?: 'warning' | 'info', children: any }} props */
function ChannelNotice({ tone = 'warning', children }) {
  return html`<div class="channel-notice" data-tone=${tone} role=${tone === 'warning' ? 'alert' : 'status'}>
    <${Icon} name=${tone === 'warning' ? 'IconAlertTriangle' : 'IconInfoCircle'} size=${16} />
    <div class="channel-notice-text">${children}</div>
  </div>`;
}

function BillingNote() {
  return html`<p class="channel-card-note channel-billing-note">
    <${Icon} name="IconInfoCircle" size=${14} />
    <span>${t('metaSignup.billingNote')} <a class="integration-link" href=${WHATSAPP_MANAGER_URL} target="_blank" rel="noopener noreferrer">${t('metaSignup.billingLink')}</a></span>
  </p>`;
}

/**
 * Cartão de um número de WhatsApp ativo: dados, avisos da Meta e do registro, Registrar de novo, Atualizar conexão
 * e Desconectar.
 * @param {{ channel: Channel, isAdmin: boolean, metaSignup: { portalOrigin: string } | null, recentResult: MetaSignupResult | null,
 *   onChanged: () => void, onConnected: (result: MetaSignupResult) => void, purposeTeams: any[] | null }} props
 *   purposeTeams: departamentos do atendimento (com o módulo ligado); nulo esconde a finalidade do número
 */
function WhatsappNumberCard({ channel, isAdmin, metaSignup, recentResult, onChanged, onConnected, purposeTeams }) {
  const settings = channel.settings ?? {};
  const [isRegistering, setIsRegistering] = useState(false);
  const disconnect = async () => {
    try {
      await deleteChannel(channel.id);
      showSuccessSnackbar(t('settings.channels.whatsapp.disconnectedSnackbar'));
      onChanged();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    }
  };
  const registerAgain = async () => {
    setIsRegistering(true);
    try {
      await registerWhatsappNumber(channel.id);
      showSuccessSnackbar(t('metaSignup.registeredSnackbar'));
      onChanged();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setIsRegistering(false);
    }
  };
  const alert = settings.metaAccountAlert;
  const isRecentCoexistence = recentResult?.isCoexistence && recentResult.channelId === channel.id;
  return html`<div class="channel-card" data-channel-id=${channel.id}>
    <div class="channel-card-header">
      <${ChannelIcon} channelType="WHATSAPP" size=${24} />
      <div class="channel-card-titles">
        <span class="channel-card-title">${settings.displayPhoneNumber ?? channel.handle}</span>
        <span class="channel-card-description">${settings.verifiedName ?? channel.displayName ?? ''}</span>
        ${settings.isCoexistence ? html`<span class="channel-badge"><${Icon} name="IconPhone" size=${12} />${t('metaSignup.businessApp')}</span>` : null}
      </div>
      <${ConnectionStatus} isConnected=${true} />
    </div>
    <dl class="channel-card-details">
      <div><dt>${t('settings.channels.whatsapp.number')}</dt><dd>${settings.displayPhoneNumber ?? channel.handle}</dd></div>
      <div><dt>${t('settings.channels.whatsapp.verifiedName')}</dt><dd>${settings.verifiedName ?? channel.displayName ?? ''}</dd></div>
      ${settings.phoneNumberId ? html`<div><dt>${t('settings.channels.whatsapp.phoneNumberId')}</dt><dd class="channel-card-mono">${settings.phoneNumberId}</dd></div>` : null}
      ${settings.wabaId ? html`<div><dt>${t('settings.channels.whatsapp.wabaId')}</dt><dd class="channel-card-mono">${settings.wabaId}</dd></div>` : null}
    </dl>
    ${purposeTeams ? html`<${ChannelPurposeField} channel=${channel} teams=${purposeTeams} canEdit=${canChangeChannelPurpose()} onChanged=${onChanged} />` : null}
    <${WhatsappMessagingLimit} channelId=${channel.id} hasWaba=${Boolean(settings.wabaId)} />
    <${OptOutConfirmationToggle} channelId=${channel.id} settings=${settings} isAdmin=${isAdmin} />
    ${settings.registration === 'FAILED'
      ? html`<${ChannelNotice}>
          <span>${t('metaSignup.warnings.REGISTRATION_FAILED')}</span>
          ${isAdmin ? html`<${Button} title=${t('metaSignup.registerAgain')} icon="IconRefresh" variant="secondary" size="small" isLoading=${isRegistering} onClick=${registerAgain} />` : null}
        <//>`
      : null}
    ${settings.coexistence?.appDataSync === 'FAILED' ? html`<${ChannelNotice}><span>${t('metaSignup.warnings.APP_DATA_SYNC_FAILED')}</span><//>` : null}
    ${isRecentCoexistence ? html`<${ChannelNotice} tone="info"><span>${t('metaSignup.coexistenceHistory')}</span><//>` : null}
    ${alert?.event
      ? html`<${ChannelNotice}><span>${t('metaSignup.accountAlert', { event: hasTranslation(`metaSignup.accountAlertEvents.${alert.event}`) ? t(`metaSignup.accountAlertEvents.${alert.event}`) : alert.event })}</span><//>`
      : null}
    ${isAdmin ? html`<${BillingNote} />` : null}
    ${isAdmin
      ? html`<div class="channel-card-actions">
          ${metaSignup ? html`<${MetaSignupConnect} metaSignup=${metaSignup} title=${t('metaSignup.updateConnection')} variant="secondary" icon="IconRefresh" onConnected=${onConnected} />` : null}
          <${Button} title=${t('settings.channels.whatsapp.disconnect')} icon="IconUnlink" variant="secondary" accent="danger" size="small" onClick=${disconnect} />
        </div>`
      : null}
  </div>`;
}

/**
 * Número que a Meta desconectou (account_update): aviso e reconexão.
 * @param {{ channel: import('../../data/conversations.js').InactiveChannel, isAdmin: boolean, metaSignup: { portalOrigin: string } | null,
 *   onConnected: (result: MetaSignupResult) => void }} props
 */
function InactiveWhatsappCard({ channel, isAdmin, metaSignup, onConnected }) {
  const event = channel.settings?.deactivation?.event ?? 'UNKNOWN';
  const reasonKey = `metaSignup.deactivationReasons.${event}`;
  return html`<div class="channel-card" data-channel-id=${channel.id} data-inactive="true">
    <div class="channel-card-header">
      <${ChannelIcon} channelType="WHATSAPP" size=${24} />
      <div class="channel-card-titles">
        <span class="channel-card-title">${channel.settings?.displayPhoneNumber ?? channel.handle}</span>
        <span class="channel-card-description">${channel.displayName ?? ''}</span>
      </div>
      <${ConnectionStatus} isConnected=${false} label=${t('settings.channels.whatsapp.inactive')} />
    </div>
    <${ChannelNotice}>
      <strong>${t('metaSignup.inactiveTitle')}</strong>
      <span>${t('metaSignup.inactive', { reason: t(hasTranslation(reasonKey) ? reasonKey : 'metaSignup.deactivationReasons.UNKNOWN') })}</span>
    <//>
    ${isAdmin && metaSignup ? html`<div class="channel-card-actions"><${MetaSignupConnect} metaSignup=${metaSignup} onConnected=${onConnected} /></div>` : null}
  </div>`;
}

/**
 * Conexão manual com os dados do painel da Meta (E2): recolhida quando a conexão pela Meta está disponível.
 * @param {{ isOpenByDefault: boolean, showNotConfigured: boolean, onChanged: () => void }} props
 */
function ManualWhatsappConnection({ isOpenByDefault, showNotConfigured, onChanged }) {
  const [values, setValues] = useState(/** @type {Record<string, string>} */ ({}));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const webhookUrl = `${window.location.origin}/webhooks/whatsapp`;

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (WHATSAPP_FORM_FIELDS.some((name) => !String(values[name] ?? '').trim())) {
      setError(t('settings.channels.whatsapp.requiredFields'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await connectWhatsappChannel(/** @type {any} */ (Object.fromEntries(WHATSAPP_FORM_FIELDS.map((name) => [name, values[name].trim()]))));
      showSuccessSnackbar(t('settings.channels.whatsapp.connectedSnackbar'));
      setValues({});
      onChanged();
    } catch (requestError) {
      // Validação do token na Graph API: a mensagem da API diz o que corrigir.
      const detail = requestError instanceof ApiError ? requestError.messages[0] : null;
      const message = getErrorMessage(requestError);
      setError(detail && message === t('errors.UNKNOWN') ? detail : detail ? `${message} ${detail}` : message);
    } finally {
      setIsSaving(false);
    }
  };

  /** @param {(typeof WHATSAPP_FORM_FIELDS)[number]} name @param {string} [placeholder] @param {string} [type] */
  const formField = (name, placeholder, type = 'text') => html`<label class="form-field">
    <span class="form-field-label">${t(`settings.channels.whatsapp.${name}`)}</span>
    <input
      class="text-input"
      type=${type}
      value=${values[name] ?? ''}
      placeholder=${placeholder}
      autocomplete="off"
      onInput=${(event) => setValues({ ...values, [name]: /** @type {HTMLInputElement} */ (event.currentTarget).value })}
    />
  </label>`;

  return html`<details class="channel-card channel-manual" open=${isOpenByDefault}>
    <summary class="channel-manual-summary">
      <${Icon} name="IconKey" size=${18} />
      <span class="channel-card-titles">
        <span class="channel-card-title">${t('metaSignup.manualTitle')}</span>
        <span class="channel-card-description">${t('metaSignup.manualDescription')}</span>
      </span>
      <${Icon} name="IconChevronDown" size=${16} className="channel-manual-chevron" />
    </summary>
    ${showNotConfigured ? html`<${ChannelNotice} tone="info"><span>${t('metaSignup.notConfigured')}</span><//>` : null}
    <form class="channel-form" onSubmit=${submit}>
      <div class="form-field-row">
        ${formField('phoneNumberId', '109876543210987')}
        ${formField('wabaId', '209876543210987')}
      </div>
      <div class="form-field-row">
        ${formField('displayPhoneNumber', t('settings.channels.whatsapp.displayPhoneNumberPlaceholder'))}
        ${formField('verifiedName', t('settings.channels.whatsapp.verifiedNamePlaceholder'))}
      </div>
      <div>
        ${formField('accessToken', 'EAAG...', 'password')}
        <span class="input-hint">${t('settings.channels.whatsapp.accessTokenHint')}</span>
      </div>
      ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
      <div class="channel-form-actions">
        <${Button} title=${t('settings.channels.whatsapp.connect')} icon="IconPlugConnected" accent="blue" size="small" type="submit" isLoading=${isSaving} />
      </div>
    </form>
    <div class="channel-help">
      <span class="channel-card-title">${t('settings.channels.whatsapp.helpTitle')}</span>
      <ol>
        ${[1, 2, 3, 4].map((step) => html`<li key=${step}>${t(`settings.channels.whatsapp.helpSteps.step${step}`)}</li>`)}
      </ol>
      <span class="form-field-label">${t('settings.channels.whatsapp.webhookUrl')}</span>
      <${CopyableValue} value=${webhookUrl} label=${t('settings.channels.whatsapp.webhookUrl')} />
    </div>
  </details>`;
}

/**
 * Bloco do WhatsApp em Canais: um cartão por número, os desconectados pela Meta, "Conectar com a Meta" (ou "Conectar
 * outro número") e a conexão manual recolhida.
 * @param {{ channels: Channel[], inactiveChannels: import('../../data/conversations.js').InactiveChannel[], isAdmin: boolean,
 *   metaSignup: { portalOrigin: string } | null, onChanged: () => void, purposeTeams: any[] | null }} props
 */
function WhatsappChannels({ channels, inactiveChannels, isAdmin, metaSignup, onChanged, purposeTeams }) {
  const [recentResult, setRecentResult] = useState(/** @type {MetaSignupResult | null} */ (null));
  const onConnected = (/** @type {MetaSignupResult} */ result) => {
    setRecentResult(result);
    onChanged();
  };
  const hasNumbers = channels.length > 0;
  return html`<div class="whatsapp-channels">
    ${channels.map((channel) => html`<${WhatsappNumberCard} key=${channel.id} channel=${channel} isAdmin=${isAdmin} metaSignup=${metaSignup}
      recentResult=${recentResult} onChanged=${onChanged} onConnected=${onConnected} purposeTeams=${purposeTeams} />`)}
    ${inactiveChannels.map((channel) => html`<${InactiveWhatsappCard} key=${channel.id} channel=${channel} isAdmin=${isAdmin} metaSignup=${metaSignup} onConnected=${onConnected} />`)}
    ${!hasNumbers
      ? html`<div class="channel-card">
          <div class="channel-card-header">
            <${ChannelIcon} channelType="WHATSAPP" size=${24} />
            <div class="channel-card-titles">
              <span class="channel-card-title">${t('settings.channels.whatsapp.title')}</span>
              <span class="channel-card-description">${t('settings.channels.whatsapp.description')}</span>
            </div>
            <${ConnectionStatus} isConnected=${false} />
          </div>
          ${isAdmin && metaSignup
            ? html`<div class="meta-signup-start">
                <${MetaSignupConnect} metaSignup=${metaSignup} onConnected=${onConnected} />
                <span class="input-hint">${t('metaSignup.recommended')}</span>
              </div>`
            : null}
          ${!isAdmin ? html`<${AdminOnlyNote} />` : null}
        </div>`
      : null}
    ${hasNumbers && isAdmin && metaSignup
      ? html`<div class="whatsapp-channels-actions">
          <${MetaSignupConnect} metaSignup=${metaSignup} title=${t('metaSignup.connectAnother')} variant="secondary" icon="IconPlus" onConnected=${onConnected} />
        </div>`
      : null}
    ${hasNumbers && !isAdmin ? html`<${AdminOnlyNote} />` : null}
    ${isAdmin ? html`<${ManualWhatsappConnection} isOpenByDefault=${!metaSignup} showNotConfigured=${!metaSignup} onChanged=${onChanged} />` : null}
  </div>`;
}

/**
 * @param {{ address: string | null, channel?: Channel | null, purposeTeams?: any[] | null, onChanged?: () => void }} props
 */
function EmailCard({ address, channel = null, purposeTeams = null, onChanged = () => {} }) {
  return html`<div class="channel-card">
    <div class="channel-card-header">
      <${ChannelIcon} channelType="EMAIL" size=${24} />
      <div class="channel-card-titles">
        <span class="channel-card-title">${t('settings.channels.email.title')}</span>
        <span class="channel-card-description">${t('settings.channels.email.description')}</span>
      </div>
      <${ConnectionStatus} isConnected=${Boolean(address)} label=${address ? t('settings.channels.whatsapp.connected') : t('settings.channels.whatsapp.disconnected')} />
    </div>
    <div class="channel-card-section">
      <span class="form-field-label">${t('settings.channels.email.address')}</span>
      ${address
        ? html`<${CopyableValue} value=${address} label=${t('settings.channels.email.copy')} />`
        : html`<span class="channel-card-description">${t('settings.channels.email.unavailable')}</span>`}
    </div>
    ${purposeTeams && channel ? html`<${ChannelPurposeField} channel=${channel} teams=${purposeTeams} canEdit=${canChangeChannelPurpose()} onChanged=${onChanged} />` : null}
    <ul class="channel-card-explanation">
      <li><${Icon} name="IconMailForward" size=${16} /><span>${t('settings.channels.email.explanationCc')}</span></li>
      <li><${Icon} name="IconCornerUpLeft" size=${16} /><span>${t('settings.channels.email.explanationReplies')}</span></li>
    </ul>
  </div>`;
}

/** Mudar a finalidade do número pede a configuração da instituição e a do atendimento (seção 4.1). */
const canChangeChannelPurpose = () => hasCurrentPermissionFlag('WORKSPACE') && hasCurrentPermissionFlag('SERVICE_DESK_SETTINGS');

export function SettingsChannelsPage() {
  const [data, setData] = useState(/** @type {Awaited<ReturnType<typeof listChannels>> | null} */ (null));
  // departamentos do atendimento para a finalidade do número; nulo sem o módulo ou sem as flags do atendimento
  const [purposeTeams, setPurposeTeams] = useState(/** @type {any[] | null} */ (null));
  // conectar e remover canais é configuração da instituição (flag WORKSPACE do papel)
  const isAdmin = hasCurrentPermissionFlag('WORKSPACE');

  const load = () => {
    listChannels()
      .then(setData)
      .catch((error) => {
        showErrorSnackbar(error);
        setData({ channels: [], inactiveChannels: [], inboundEmailAddress: null, metaSignup: null });
      });
  };
  useEffect(() => {
    document.title = `${t('settings.channels.title')} - ${t('navigation.settings')}`;
    load();
    if (isTenantModuleEnabled('SERVICE_DESK') && (hasCurrentPermissionFlag('SERVICE_DESK') || hasCurrentPermissionFlag('SERVICE_DESK_SETTINGS'))) {
      listServiceTeams().then(setPurposeTeams).catch(() => setPurposeTeams(null));
    }
  }, []);

  const whatsappChannels = (data?.channels ?? []).filter((channel) => channel.type === 'WHATSAPP');
  const emailChannel = data?.channels.find((channel) => channel.type === 'EMAIL');
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconPlugConnected" title=${t('settings.channels.title')} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('settings.channels.title')} description=${t('settings.channels.description')}>
        ${data
          ? html`
              <${WhatsappChannels} channels=${whatsappChannels} inactiveChannels=${data.inactiveChannels ?? []} isAdmin=${isAdmin}
                metaSignup=${data.metaSignup ?? null} onChanged=${load} purposeTeams=${purposeTeams} />
              ${whatsappChannels.length > 0
                // conectar ou desconectar um número muda os modelos listados: o resumo recarrega
                ? html`<${WhatsappTemplatesSummary} key=${whatsappChannels.map((channel) => channel.id).join(',')} />`
                : null}
              <${EmailCard} address=${data.inboundEmailAddress ?? emailChannel?.handle ?? null} channel=${emailChannel ?? null} purposeTeams=${purposeTeams} onChanged=${load} />
              <${LeadAdsCard} isAdmin=${isAdmin} />
            `
          : html`<${Skeleton} height=${160} radius=${8} /><${Skeleton} height=${140} radius=${8} />`}
      <//>
    </div>
  <//>`;
}
